import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Chip, IconButton, Tooltip, CircularProgress,
  TablePagination, TextField, Button, Autocomplete,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import ReplayIcon from '@mui/icons-material/Replay';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useNavigate } from 'react-router-dom';
import { VentaRecord } from '../../domain/entities/VentaRecord';
import { generarTiquetePDF } from '../../application/utils/pdfGenerator';
import { useAuth } from 'react-oidc-context';
import { VentaApiService } from '../../../venta/infrastructure/api/VentaApiService';
import { useSession, Terminal, Equipo } from '../../../../core/context/SessionContext';
import { EnvioNacionalModal } from '../../../venta/presentation/components/EnvioNacionalModal/EnvioNacionalModal';
import { EnvioNacionalForm } from '../../../venta/presentation/components/EnvioNacionalModal/EnvioNacionalModal.types';

function etiquetaPedidoSap(venta: VentaRecord): string {
  const partes: string[] = [];
  if (venta.idPedidoSAP) {
    partes.push(venta.idPedidoSAP);
  }
  for (const guia of venta.guias ?? []) {
    if (guia && !partes.includes(guia)) {
      partes.push(guia);
    }
  }
  if (partes.length > 0) {
    return partes.join(', ');
  }
  if (venta.compraId) {
    return `Compra ${venta.compraId}`;
  }
  return '—';
}

interface DetalleErrorHttp {
  message: string;
  url?: string;
  method?: string;
  request?: unknown;
  response?: unknown;
}

function parseErrorMensaje(raw: string): DetalleErrorHttp {
  try {
    const parsed = JSON.parse(raw) as {
      message?: string;
      request?: { method?: string; url?: string; body?: unknown };
      response?: unknown;
    };
    if (parsed && typeof parsed === 'object') {
      return {
        message: parsed.message ?? raw,
        url: parsed.request?.url,
        method: parsed.request?.method,
        request: parsed.request?.body ?? parsed.request,
        response: parsed.response,
      };
    }
  } catch {
    // Mensajes históricos que no son JSON.
  }
  return { message: raw };
}

function formatearJson(valor: unknown): string {
  if (valor === undefined || valor === null) {
    return '—';
  }
  if (typeof valor === 'string') {
    try {
      return JSON.stringify(JSON.parse(valor), null, 2);
    } catch {
      return valor;
    }
  }
  return JSON.stringify(valor, null, 2);
}

function CodeBlock({ children }: Readonly<{ children: string }>) {
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1.5,
        borderRadius: 1,
        bgcolor: '#0d1117',
        color: '#e6edf3',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        fontSize: 12,
        lineHeight: 1.5,
        overflow: 'auto',
        maxHeight: 260,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {children}
    </Box>
  );
}

function EstadoPedidoCell({
  venta,
  onVerDetalle,
  detalleTecnico,
}: Readonly<{
  venta: VentaRecord;
  onVerDetalle: (errorMensaje: string) => void;
  detalleTecnico: boolean;
}>) {
  const fallido = venta.estado === 'FALLIDO';
  const puedeAbrirDetalle = detalleTecnico && Boolean(venta.errorMensaje);

  if (fallido) {
    return (
      <Chip
        label="Fallido"
        size="small"
        onClick={puedeAbrirDetalle ? () => onVerDetalle(venta.errorMensaje as string) : undefined}
        sx={{
          fontWeight: 'bold',
          bgcolor: '#d32f2f',
          color: '#fff',
          cursor: puedeAbrirDetalle ? 'pointer' : 'default',
          '&:hover': puedeAbrirDetalle ? { bgcolor: '#b71c1c' } : undefined,
        }}
      />
    );
  }

  if (venta.estado === 'COMPLETADO') {
    return (
      <Chip
        label="Completado"
        size="small"
        sx={{ fontWeight: 'bold', bgcolor: '#2e7d32', color: '#fff' }}
      />
    );
  }

  return (
    <Chip
      label={venta.estado ?? '—'}
      size="small"
      sx={{ fontWeight: 'bold' }}
    />
  );
}

function formatearRequest(detalle: DetalleErrorHttp): string {
  const body = formatearJson(detalle.request);
  if (detalle.method) {
    return `${detalle.method}\n\n${body}`;
  }
  return body;
}

function DialogDetalleError({
  errorMensaje,
  onClose,
}: Readonly<{
  errorMensaje: string | null;
  onClose: () => void;
}>) {
  const detalle = errorMensaje ? parseErrorMensaje(errorMensaje) : null;
  const requestCode = detalle ? formatearRequest(detalle) : '';

  return (
    <Dialog open={!!errorMensaje} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold' }}>Detalle del fallo</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        {detalle && (
          <>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Mensaje</Typography>
              <CodeBlock>{detalle.message}</CodeBlock>
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>URL</Typography>
              <CodeBlock>{detalle.url ?? '—'}</CodeBlock>
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Request</Typography>
              <CodeBlock>{requestCode || '—'}</CodeBlock>
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Response</Typography>
              <CodeBlock>{formatearJson(detalle.response)}</CodeBlock>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained" color="secondary">Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

export const HistorialPage: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { terminal, equipo } = useSession();
  const moneda: 'COP' | 'MXN' = terminal?.pais === 'MX' ? 'MXN' : 'COP';
  const esDG = terminal?.codigo_terminal === 10;

  const [historial, setHistorial] = useState<VentaRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorCarga, setErrorCarga] = useState<string | null>(null);
  const [imprimiendoId, setImprimiendoId] = useState<number | null>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const today = new Date().toISOString().split('T')[0];

  const [modalRepetirOpen, setModalRepetirOpen] = useState(false);
  const [envioParaRepetir, setEnvioParaRepetir] = useState<EnvioNacionalForm | null>(null);
  const [detalleError, setDetalleError] = useState<string | null>(null);
  const isProd = globalThis.location.hostname === 'sigo-droop.coordinadora.com';
  const [tieneUltimoEnvio] = useState(
    () => localStorage.getItem('ultimo_envio_nacional') !== null
  );

  // Filtros DG
  const [terminales, setTerminales] = useState<Terminal[]>([]);
  const [equipos, setEquipos] = useState<Equipo[]>([]);
  const [selectedTerminal, setSelectedTerminal] = useState<Terminal | null>(null);
  const [selectedEquipo, setSelectedEquipo] = useState<Equipo | null>(null);

  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [activeFilters, setActiveFilters] = useState<{
    startDate: string;
    endDate: string;
    terminal?: number;
    equipo?: number;
  }>({ startDate: '', endDate: '' });

  // Carga historial
  useEffect(() => {
    const cargarHistorial = async () => {
      setIsLoading(true);
      try {
        if (auth.user?.access_token) {
          const result = await VentaApiService.obtenerHistorialBD(
            auth.user.access_token,
            terminal?.pais ?? '',
            page + 1,
            rowsPerPage,
            {
              startDate: activeFilters.startDate,
              endDate: activeFilters.endDate,
              terminal: esDG ? activeFilters.terminal : terminal?.codigo_terminal,
              equipo: esDG ? activeFilters.equipo : equipo?.codigo_equipo,
            }
          );
          setHistorial(result.data);
          setTotalRecords(result.total);
        }
      } catch (error: unknown) {
        const detail = error instanceof Error ? error.message : 'Error desconocido';
        setErrorCarga(`No se pudo cargar el historial: ${detail}`);
      } finally {
        setIsLoading(false);
      }
    };

    cargarHistorial();
  }, [auth.user?.access_token, page, rowsPerPage, activeFilters, esDG, terminal?.codigo_terminal, equipo?.codigo_equipo, terminal?.pais]);

  // Carga terminales (solo DG)
  useEffect(() => {
    if (!esDG || !auth.user?.access_token) return;
    VentaApiService.obtenerTerminalesBD(auth.user.access_token, terminal?.pais)
      .then(setTerminales)
      .catch(() => {});
  }, [esDG, auth.user?.access_token, terminal?.pais]);

  // Carga equipos al seleccionar terminal (solo DG)
  useEffect(() => {
    if (!selectedTerminal || !auth.user?.access_token) {
      setEquipos([]);
      setSelectedEquipo(null);
      return;
    }
    VentaApiService.obtenerEquiposBD(selectedTerminal.codigo_terminal, auth.user.access_token, terminal?.pais)
      .then(setEquipos)
      .catch(() => {});
  }, [selectedTerminal, auth.user?.access_token, terminal?.pais]);

  const handleRepetirEnvio = () => {
    const raw = localStorage.getItem('ultimo_envio_nacional');
    if (!raw) return;
    setEnvioParaRepetir(JSON.parse(raw) as EnvioNacionalForm);
    setModalRepetirOpen(true);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleApplyFilters = () => {
    setPage(0);
    setActiveFilters({
      startDate,
      endDate,
      terminal: selectedTerminal?.codigo_terminal,
      equipo: selectedEquipo?.codigo_equipo,
    });
  };

  const handleClearFilters = () => {
    setStartDate(today);
    setEndDate(today);
    setSelectedTerminal(null);
    setSelectedEquipo(null);
    setPage(0);
    setActiveFilters({ startDate: '', endDate: '' });
  };

  const handleImprimirTiquete = async (venta: VentaRecord) => {
    if (!auth.user?.access_token || !venta.compraId) return;
    setImprimiendoId(venta.compraId);
    try {
      // DG (codigo_terminal 10) es un terminal administrativo sin ventas propias.
      // Debe usar el terminal real de la compra (venta.codigoTerminal) para que
      // el backend valide correctamente. Terminales normales usan su propio código.
      const codigoTerminalTiquete = esDG ? venta.codigoTerminal : terminal?.codigo_terminal;
      const datosTiquete = await VentaApiService.obtenerTiqueteBD(venta.compraId, auth.user.access_token, terminal?.pais ?? '', codigoTerminalTiquete);
      // Backend includes MX tax breakdown (baseGravable, ivaEnvios, etc.) in the tiquete response.
      // The spread of datosTiquete is sufficient; no frontend recalculation needed.
      generarTiquetePDF({ ...datosTiquete, moneda });
    } catch (error: unknown) {
      const detail = error instanceof Error ? error.message : 'Error desconocido';
      alert(`No se pudo obtener la información del tiquete: ${detail}`);
    } finally {
      setImprimiendoId(null);
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

  const formatDate = (dateString: string) =>
    new Intl.DateTimeFormat('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateString));

  const hasActiveFilter =
    activeFilters.startDate ||
    activeFilters.endDate ||
    activeFilters.terminal !== undefined ||
    activeFilters.equipo !== undefined;

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Historial de Ventas
        </Typography>
        {tieneUltimoEnvio && !isProd && (
          <Button
            variant="outlined"
            startIcon={<ReplayIcon />}
            onClick={handleRepetirEnvio}
          >
            Repetir último envío
          </Button>
        )}
      </Box>

      <Paper elevation={1} sx={{ p: 2, mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          label="Fecha Inicio"
          type="date"
          size="small"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ flex: 1, minWidth: 150 }}
        />
        <TextField
          label="Fecha Fin"
          type="date"
          size="small"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ flex: 1, minWidth: 150 }}
        />

        {esDG && (
          <>
            <Autocomplete
              size="small"
              sx={{ flex: 1, minWidth: 200 }}
              options={terminales}
              value={selectedTerminal}
              getOptionLabel={(t) => `${t.nombre_terminal} (Cód: ${t.codigo_terminal})`}
              filterOptions={(opts, { inputValue }) => {
                const q = inputValue.toLowerCase();
                return opts.filter(t =>
                  t.nombre_terminal.toLowerCase().includes(q) ||
                  String(t.codigo_terminal).includes(q)
                );
              }}
              onChange={(_, val) => {
                setSelectedTerminal(val);
                setSelectedEquipo(null);
              }}
              isOptionEqualToValue={(opt, val) => opt.codigo_terminal === val.codigo_terminal}
              renderInput={(params) => <TextField {...params} label="Terminal" />}
            />
            <Autocomplete
              size="small"
              sx={{ flex: 1, minWidth: 180 }}
              options={equipos}
              value={selectedEquipo}
              disabled={!selectedTerminal}
              getOptionLabel={(e) => e.nombre_equipo}
              filterOptions={(opts, { inputValue }) => {
                const q = inputValue.toLowerCase();
                return opts.filter(e => e.nombre_equipo.toLowerCase().includes(q));
              }}
              onChange={(_, val) => setSelectedEquipo(val)}
              isOptionEqualToValue={(opt, val) => opt.codigo_equipo === val.codigo_equipo}
              renderInput={(params) => <TextField {...params} label="Equipo" />}
            />
          </>
        )}

        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={handleApplyFilters}
          disabled={isLoading}
        >
          Filtrar
        </Button>
        {hasActiveFilter && (
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ClearIcon />}
            onClick={handleClearFilters}
            disabled={isLoading}
          >
            Limpiar
          </Button>
        )}
      </Paper>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
          <CircularProgress />
        </Box>
      )}
      {!isLoading && errorCarga && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
          <Typography color="error">{errorCarga}</Typography>
        </Box>
      )}
      {!isLoading && !errorCarga && (
        <TableContainer component={Paper} elevation={3} sx={{ flexGrow: 1, minHeight: 0, overflow: 'auto', borderRadius: 2 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>N° Venta</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Pedido SAP/Guía</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Terminal / Equipo</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Método Pago</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Total</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {historial.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">
                      No hay ventas registradas en el historial.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                historial.map((venta) => (
                  <TableRow key={`${venta.compraId ?? 'na'}-${venta.idPedidoSAP ?? 'na'}-${venta.fecha}`} hover>
                    <TableCell>
                      <Chip
                        label={venta.compraId ?? '—'}
                        color="primary"
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={etiquetaPedidoSap(venta)}
                        color="primary"
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </TableCell>
                    <TableCell>
                      <EstadoPedidoCell
                        venta={venta}
                        onVerDetalle={setDetalleError}
                        detalleTecnico={esDG}
                      />
                    </TableCell>
                    <TableCell>{formatDate(venta.fecha)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {venta.cliente.nombre || 'Público en General'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {venta.cliente.tipoDocumento && venta.cliente.documento
                          ? `${venta.cliente.tipoDocumento} ${venta.cliente.documento}`
                          : 'RFC XAXX01010100'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{venta.terminal}</Typography>
                      <Typography variant="caption" color="text.secondary">Eq: {venta.equipo}</Typography>
                    </TableCell>
                    <TableCell>{venta.metodoPago}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(venta.total)}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Imprimir Tiquete">
                        <span>
                          <IconButton
                            aria-label="Imprimir Tiquete"
                            color="secondary"
                            onClick={() => handleImprimirTiquete(venta)}
                            size="small"
                            disabled={!venta.compraId || imprimiendoId === venta.compraId}
                          >
                            {imprimiendoId !== null && imprimiendoId === venta.compraId
                              ? <CircularProgress size={20} color="inherit" />
                              : <PrintIcon />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <DialogDetalleError
        errorMensaje={detalleError}
        onClose={() => setDetalleError(null)}
      />

      {auth.user?.access_token && (
        <EnvioNacionalModal
          open={modalRepetirOpen}
          onClose={() => setModalRepetirOpen(false)}
          accessToken={auth.user.access_token}
          initialForm={envioParaRepetir ?? undefined}
          initialStep={3}
          onAgregarAlCarrito={() => navigate('/venta')}
        />
      )}

      <TablePagination
        component="div"
        sx={{ flexShrink: 0 }}
        count={totalRecords}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Filas por página:"
        labelDisplayedRows={({ from, to, count }) => {
          const total = count === -1 ? `más de ${to}` : String(count);
          return `${from}-${to} de ${total}`;
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        disabled={isLoading}
      />
    </Box>
  );
};
